import React, { createContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import useAxios from "../../hooks/useAxios";

//helpers and hooks
import { FormatFiltersQuery, updateUrlParams, parseUrlParams } from "../Filters/FilteringHelpers";

const MediaCentreContext = createContext();

//Filters API:
//https://americanrivers.local/wp-json/cneheritage/v1/filters?type=media-items

// Handles global states and state updates for the entire Media Center index app

export const AppProvider = ({ children }) => {
	// used to display the selected filters, it also accounts for sharable filters
	const [selectedFilters, setSelectedFilters] = useState(
		location.search ? parseUrlParams(location.search) : []
	);
	const [currentPageNumber, setCurrentPageNumber] = useState(
		location.hash ? parseInt(window.location.hash.replace("#", "")) : 1
	);
	const [mediaItemUrl, setMediaItemUrl] = useState(
		`media-items?page=${currentPageNumber}${FormatFiltersQuery(selectedFilters)}`
	);

	// fetch all the filters from the API
	const { response: filters } = useAxios("filters?type=media-item");
	const { response: mediaItems, totalPages } = useAxios(mediaItemUrl);

	useEffect(() => {
		// request doesnt get called twice on initial because the string should match the
		setMediaItemUrl(
			`media-items?page=${currentPageNumber}${FormatFiltersQuery(selectedFilters)}`
		);
	}, [selectedFilters, currentPageNumber]);

	useEffect(() => {
		const isPreview = location.search.includes("page_id");
		
		// don't execute when page is in preview mode
		if (isPreview) return;

		// every time the selectedFilters update we update the browser URL to match
		updateUrlParams(selectedFilters, currentPageNumber);
	}, [selectedFilters, currentPageNumber]);

	// onClick handler for selecting and deselecting a filter
	// this function formats the selectedFilters into a specific data structure
	// that we'll use for the constructing the filters query for the api call
	const handleFilterSelect = (filter) => {
		let selectedFiltersCopy = [...selectedFilters];

		// we get the taxonomy index in the array
		let existingTaxonomyIndex = selectedFiltersCopy.findIndex(
			(t) => t.taxonomy == filter.taxonomy
		);

		// check if taxonomy exists
		if (existingTaxonomyIndex >= 0) {
			// get index of the child filter
			let existingFilterIndex = selectedFiltersCopy[
				existingTaxonomyIndex
			].filters.findIndex((f) => f.slug === filter.filters[0].slug);

			let childFilters = selectedFiltersCopy[existingTaxonomyIndex].filters;

			// check if child filter exists
			if (existingFilterIndex >= 0) {
				// if child filter exists we remove it using the index
				childFilters.splice(existingFilterIndex, 1);

				// if there's no child filters left we remove the whole taxonomy
				if (childFilters.length === 0) {
					selectedFiltersCopy.splice(existingTaxonomyIndex, 1);
				}
			} else {
				// we dont have the child filter so we push it into the array of child filters inside taxonomy object
				childFilters.push(filter.filters[0]);
			}
		} else {
			selectedFiltersCopy = [...selectedFiltersCopy, filter];
		}

		// we reset the page everytime a filter is selected and deselected
		setCurrentPageNumber(1);
		setSelectedFilters(selectedFiltersCopy);
	};

	// onClick handler for selecting and deselecting a filter
	const clearAllFilters = () => {
		setSelectedFilters([]);
		setCurrentPageNumber(1);
		window.history.pushState({}, "", window.location.pathname);
	};

	const scrollToTop = () => {
		return window.scrollTo({ top: 0, left: 0, behaviour: "smooth" });
	};

	// Pagination helpers actions
	const handleIncrementPage = () => {
		if (currentPageNumber < totalPages.length) {
			setCurrentPageNumber((currentNumber) => currentNumber + 1);
			scrollToTop();
		}
	};
	const handleDecrementPage = () => {
		if (currentPageNumber > 1) {
			setCurrentPageNumber((currentNumber) => currentNumber - 1);
			scrollToTop();
		}
	};
	const handleButtonPageSelect = (buttonNumber) => {
		setCurrentPageNumber(buttonNumber);
		scrollToTop();
	};

	const data = {
		selectedFilters,
		filters,
		mediaItems,
		totalPages,
		currentPageNumber,
	};

	const actions = {
		handleFilterSelect,
		clearAllFilters,
		handleIncrementPage,
		handleDecrementPage,
		handleButtonPageSelect,
	};

	return (
		<MediaCentreContext.Provider value={{ actions, data }}>{children}</MediaCentreContext.Provider>
	);
};

AppProvider.propTypes = {
	children: PropTypes.node,
};
export default MediaCentreContext;
